import apiClient from './client'
import type { Alert, AlertEvent, CreateAlertRequest } from '../types'

interface AlertEventsParams {
  limit?: number;
  offset?: number;
  severity?: string;
}

export const alertsApi = {
  getAlerts: async (): Promise<Alert[]> => {
    const response = await apiClient.get('/alerts/')
    return response.data
  },

  getAlertEvents: async (params: AlertEventsParams = {}): Promise<AlertEvent[]> => {
    const response = await apiClient.get('/alerts/events', { params })
    return response.data.events || []
  },

  createAlert: async (data: CreateAlertRequest): Promise<Alert> => {
    const response = await apiClient.post('/alerts/', data)
    return response.data
  },

  updateAlert: async (alertId: string, data: Partial<CreateAlertRequest>): Promise<Alert> => {
    const response = await apiClient.patch(`/alerts/${alertId}`, data)
    return response.data
  },

  deleteAlert: async (alertId: string): Promise<void> => {
    await apiClient.delete(`/alerts/${alertId}`)
  },

  markRead: async (eventId: string): Promise<AlertEvent> => {
    const response = await apiClient.patch(`/alerts/events/${eventId}/read`)
    return response.data
  },

  markAllRead: async (): Promise<{ count: number }> => {
    const response = await apiClient.post('/alerts/events/read-all')
    return response.data
  },

  // Phase 13 - Enhanced alert types
  getAlertTypes: async (): Promise<AlertTypeSchema[]> => {
    const response = await apiClient.get('/alerts/types')
    return response.data
  },

  testAlert: async (alertId: string): Promise<{ status: string; message: string }> => {
    const response = await apiClient.post(`/alerts/test/${alertId}`)
    return response.data
  },

  // Phase 12 - Alert enhancements
  snoozeAlert: async (alertId: string, minutes: number = 60): Promise<Alert> => {
    const response = await apiClient.post(`/alerts/${alertId}/snooze`, { minutes })
    return response.data
  },

  unsnoozeAlert: async (alertId: string): Promise<Alert> => {
    const response = await apiClient.post(`/alerts/${alertId}/unsnooze`)
    return response.data
  },

  duplicateAlert: async (alertId: string): Promise<Alert> => {
    const response = await apiClient.post(`/alerts/${alertId}/duplicate`)
    return response.data
  },

  bulkCreate: async (alerts: CreateAlertRequest[]): Promise<Alert[]> => {
    const response = await apiClient.post('/alerts/bulk', { alerts })
    return response.data
  },

  autoEarnings: async (options: AutoEarningsOptions = {}): Promise<AutoEarningsResponse> => {
    const response = await apiClient.post('/alerts/auto-earnings', options)
    return response.data
  },

  getUnreadCount: async (): Promise<number> => {
    const response = await apiClient.get('/alerts/events', { params: { limit: 1 } })
    return response.data.unread_count || 0
  },
}

// Alert type schema interface
export interface AlertTypeSchema {
  type: string
  name: string
  description: string
  icon: string
  config_schema: Record<string, {
    type: string
    required?: boolean
    default?: unknown
    enum?: string[]
  }>
}

export interface AutoEarningsOptions {
  days_before?: number
  include_watchlist?: boolean
  include_portfolio?: boolean
  tickers?: string[]
}

export interface AutoEarningsResponse {
  created: number
  skipped: number
  tickers: string[]
}

export default alertsApi
